import type { ChangeEvent } from 'react';
import { slugify } from '../lib/slugify';
import { Tooltip, type TooltipValue } from './Tooltip';

export interface SelectOption<T extends string> {
  value: T;
  label: string;
}

export interface SelectProps<T extends string> {
  label: string;
  value: T;
  options: SelectOption<T>[];
  onChange: (value: T) => void;
  description?: string;
  /** v6 — tooltip shown on hover/focus next to the label. */
  tooltip?: TooltipValue;
}

export function Select<T extends string>(props: SelectProps<T>) {
  const { label, value, options, onChange, description, tooltip } = props;
  const id = `select-${slugify(label)}`;

  const handleChange = (e: ChangeEvent<HTMLSelectElement>) => {
    onChange(e.target.value as T);
  };

  const labelNode = (
    <label
      htmlFor={id}
      className="text-sm font-medium text-neutral-800 dark:text-neutral-200"
    >
      {label}
    </label>
  );

  return (
    <div className="block">
      {tooltip ? <Tooltip value={tooltip}>{labelNode}</Tooltip> : labelNode}
      <select
        id={id}
        name={id}
        value={value}
        onChange={handleChange}
        aria-label={description ?? label}
        title={description ?? label}
        className="mt-2 h-11 w-full cursor-pointer rounded-md border border-neutral-300 bg-white px-3 text-sm text-neutral-900 focus:border-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}
